/**
 * @description Exibe a versão do aplicativo e o nome do aplicativo
 * @module VersionViewer
 * @since Agosto 2025
 */

import React from "react";
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { useContractContext } from "@/contexts/ContractContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { Handshake, Menu, MenuOpen } from "@mui/icons-material";
import { SignatureControls } from "./SignatureControls";
import { HeaderOrganizationSelect } from "./HeaderOrganizationSelect";
import { HeaderNotification } from "./HeaderNotification";
import { AvatarMenu } from "./AvatarMenu";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { HeaderImage } from "./HeaderImage";
import { currentFeatures } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import FluxvetLogo from "@/assets/fluxmed-logo-horizontal-white.png";
import {
  getNextPaymentDueDate,
  isFluxPayContract as isFluxPayContractUtil,
  isFreePlanContract,
} from "@/utils/contract";

const LayoutPrivateHeaderContent: React.FC<{
  handleDrawerToggle: () => void;
  avatarMenuAnchorEl: null | HTMLElement;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  isMediumScreen: boolean;
  isSidebarOpen?: boolean;
}> = ({
  handleDrawerToggle,
  avatarMenuAnchorEl,
  handleMenuOpen,
  handleMenuClose,
  isMediumScreen,
  isSidebarOpen,
}: {
  handleDrawerToggle: () => void;
  avatarMenuAnchorEl: null | HTMLElement;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  isMediumScreen: boolean;
  isSidebarOpen?: boolean;
}) => {
  const theme = useTheme();
  const { typeOrganization } = useOrganizationContext();
  const { user, roles, access, loading } = useSessionContext();
  const { currentContract } = useContractContext();
  const contract = currentContract?.[0]?.resource;

  // Contratos gratuitos: não possuem próxima cobrança (`nextPaymentDueDate`)
  // e devem exibir apenas indicação de plano + possibilidade de upgrade.
  const isFreePlan = isFreePlanContract(contract);

  // Data de emissão do contrato (term.issued)
  const contractIssuedDate = dayjs(
    contract?.term?.[0]?.issued
  );

  // Verifica se o contrato é do tipo fluxPay
  const isFluxPayContract = isFluxPayContractUtil(contract);

  // Busca a extensão subscriptionType se existir
  const subscriptionTypeExtension =
    contract?.extension?.find((el: any) =>
      el?.url.includes("subscriptionType")
    )?.valueString;

  // Verifica se é um contrato de período grátis:
  // Tipo "bus" E subscriptionType contém "Profissional autônomo - Período grátis"
  // Esse tipo de contrato tem 30 dias de validade a partir da data de emissão
  const isFreeTrialContract =
    typeOrganization === "bus" &&
    subscriptionTypeExtension?.includes(
      "Profissional autônomo - Período grátis"
    );

  // Busca a extensão nextPaymentDueDate se existir
  const nextPaymentDueDateExtension = getNextPaymentDueDate(contract);

  // Verifica se é tipo "bus" sem extensão nextPaymentDueDate (exceto fluxPay e período grátis)
  const isBusWithoutNextPayment =
    typeOrganization === "bus" &&
    !nextPaymentDueDateExtension &&
    !isFluxPayContract &&
    !isFreeTrialContract;

  // Calcula a data do próximo pagamento:
  // - Se for período grátis: usa issued + 30 dias
  // - Se houver extensão nextPaymentDueDate: usa a extensão
  // - Se for tipo "bus" sem extensão: usa issued + 30 dias
  // - Para outros tipos sem extensão: retorna null
  const nextPayment = (() => {
    // Período grátis sempre usa issued + 30 dias
    if (isFreeTrialContract && contractIssuedDate.isValid()) {
      return contractIssuedDate.add(30, "day");
    }
    // Se houver extensão, usa a extensão
    if (nextPaymentDueDateExtension) {
      return dayjs(nextPaymentDueDateExtension);
    }
    // Se for tipo "bus" e não houver extensão, usa issued + 30 dias
    if (typeOrganization === "bus" && contractIssuedDate.isValid()) {
      return contractIssuedDate.add(30, "day");
    }
    // Para outros tipos sem extensão, retorna null (não exibe aviso)
    return null;
  })();

  // Calcula dias desde a emissão do contrato (para período grátis e tipo "bus")
  const daysSinceIssued =
    (isFreeTrialContract || typeOrganization === "bus") &&
    contractIssuedDate.isValid()
      ? dayjs().diff(contractIssuedDate, "day")
      : null;

  // Calcula dias restantes até o próximo pagamento
  const daysRemaining = nextPayment ? nextPayment.diff(dayjs(), "day") : null;

  // Verifica se passaram 30 dias do issued (para período grátis e tipo "bus")
  const hasPassed30Days = daysSinceIssued !== null && daysSinceIssued >= 30;

  // Define se deve exibir o aviso de teste grátis
  // Não exibe se for contrato fluxPay (fluxPay está sempre ativo)
  // Para período grátis ou tipo "bus" sem nextPaymentDueDate: sempre exibe
  // Para outros tipos: avisa se está dentro de 15 dias do pagamento e ainda há dias restantes
  const shouldShowTrialWarning = (() => {
    if (access?.type !== "professional" || !currentContract) {
      return false;
    }

    // Plano gratuito: não exibe aviso de "renovar/teste grátis". Exibe apenas indicação de plano.
    if (isFreePlan) {
      return false;
    }

    // Não exibe aviso se for contrato fluxPay (fluxPay está sempre ativo)
    if (isFluxPayContract) {
      return false;
    }

    // Período grátis: sempre exibe se a data de emissão for válida
    if (isFreeTrialContract) {
      return contractIssuedDate.isValid();
    }

    // Para tipo "bus" sem extensão nextPaymentDueDate: sempre exibe
    if (isBusWithoutNextPayment) {
      return contractIssuedDate.isValid();
    }

    // Para outros tipos: precisa ter nextPayment válido
    if (!nextPayment) {
      return false;
    }

    // Para outros tipos: avisa se está dentro de 15 dias do pagamento e ainda há dias restantes
    const daysUntilPayment = nextPayment.diff(contractIssuedDate, "day");
    return (
      daysUntilPayment <= 15 && daysRemaining !== null && daysRemaining > 0
    );
  })();

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: currentFeatures?.defaultApp
          ? "primary800.main"
          : "#00A884",
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      <Toolbar>
        {!currentFeatures.register && (
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            {isSidebarOpen ? <MenuOpen /> : <Menu />}
          </IconButton>
        )}

        <Stack direction="row" alignItems="center" spacing={3}>
          {currentFeatures?.defaultApp ? (
            <HeaderImage />
          ) : (
            <img
              src={FluxvetLogo}
              alt="Fluxvet Logo"
              style={{ height: "60px" }}
            />
          )}

          {access?.type === "professional" && currentFeatures?.defaultApp && (
            <SignatureControls />
          )}

          {access?.type === "professional" &&
            currentFeatures?.defaultApp &&
            isFreePlan && (
              <Link to="/clinic#signature" style={{ textDecoration: "none" }}>
                <Tooltip title="Você está no plano gratuito. Para aumentar limites e liberar recursos adicionais, faça um upgrade do plano.">
                  <Stack
                    direction="row"
                    alignItems="center"
                    border="1px solid"
                    borderColor="neutral0.main"
                    padding={1}
                    borderRadius={2}
                    spacing={1}
                    sx={{
                      "&:hover": {
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Handshake sx={{ color: "neutral0.main" }} />
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      textTransform="uppercase"
                      sx={{
                        color: "neutral0.main",
                        textShadow: "1px 1px 1px black",
                      }}
                    >
                      Plano gratuito
                    </Typography>
                  </Stack>
                </Tooltip>
              </Link>
            )}

          {shouldShowTrialWarning && (
            <Link to="/clinic#signature" style={{ textDecoration: "none" }}>
              <Tooltip
                title={
                  hasPassed30Days
                    ? "O período de teste grátis de 30 dias foi concluído. Realize uma nova assinatura para continuar utilizando o sistema."
                    : isFreeTrialContract || isBusWithoutNextPayment
                    ? "Após 30 dias do início do período grátis será necessário realizar uma nova assinatura."
                    : "Após esse período será debitado em sua conta o valor do plano solicitado."
                }
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  border="1px solid"
                  borderColor="neutral0.main"
                  padding={1}
                  borderRadius={2}
                  spacing={1}
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  <Handshake sx={{ color: "neutral0.main" }} />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    textTransform="uppercase"
                    sx={{
                      color: "neutral0.main",
                      textShadow: "1px 1px 1px black",
                    }}
                  >
                    {hasPassed30Days
                      ? "Renove sua assinatura"
                      : isFreeTrialContract || isBusWithoutNextPayment
                      ? `Teste grátis: ${
                          daysRemaining !== null ? daysRemaining : 0
                        } ${
                          daysRemaining === 1
                            ? "dia restante"
                            : "dias restantes"
                        }`
                      : `Teste grátis: ${daysRemaining} ${
                          daysRemaining === 1
                            ? "dia restante"
                            : "dias restantes"
                        }`}
                  </Typography>
                </Stack>
              </Tooltip>
            </Link>
          )}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />
        {roles &&
          roles.length > 0 &&
          access?.organizationId &&
          !currentFeatures.register && <HeaderOrganizationSelect />}

        <HeaderNotification isMobile={!isMediumScreen} />

        <AvatarMenu
          anchorEl={avatarMenuAnchorEl}
          handleMenuOpen={handleMenuOpen}
          handleMenuClose={handleMenuClose}
        />
      </Toolbar>
    </AppBar>
  );
};

export { LayoutPrivateHeaderContent };
